
import React, { forwardRef, useEffect, useState } from "react";
import { QuestionContent } from "@/types/test";
import { VideoRecorder, VideoRecorderHandle } from "../media/VideoRecorder";
import { cn } from "@/lib/utils";
import { generateSecureFileUrl } from "@/utils/secure-storage";

interface VideoQuestionProps {
  content: QuestionContent;
  response: string | null;
  onChange: (response: string | null | { videoUrl: string; audioUrl?: string }) => void;
  questionId?: string;
  testResponseId?: string;
  onUploadStateChange?: (isUploading: boolean) => void;
  onRecordingStart?: () => void;
  onRecordingStop?: () => void;
  onUploadProgress?: (questionId: string, status: 'pending' | 'complete' | 'failed', type: 'video' | 'audio') => void;
  isUploadInProgress?: boolean;
  isVideoRecorderReady?: boolean; // NEW PROP
}

export const VideoQuestion = forwardRef<VideoRecorderHandle, VideoQuestionProps>(
  ({ content, response, onChange, questionId, testResponseId, onUploadStateChange, onRecordingStart, onRecordingStop, onUploadProgress, isUploadInProgress = false, isVideoRecorderReady = true }, ref) => {
    const [isUploadingVideo, setIsUploadingVideo] = useState(false);
    const [uploadFailed, setUploadFailed] = useState(false);
    
    const handleRecordingComplete = async (payload: any) => {
      console.log('[VideoQuestion] Recording complete:', payload);

      // Phase 1: Preview URL (instant display)
      if (payload?.previewUrl) {
        console.log('[VideoQuestion] Setting preview URL');
        setPreviewUrl(payload.previewUrl);
        return;
      }

      // Phase 2: Actual paths (save to database)
      if (payload?.videoPath || payload?.audioPath) {
        console.log('[VideoQuestion] Saving paths to database');
        await onChange({
          videoUrl: payload.videoPath,
          audioUrl: payload.audioPath
        });
        
        // Notify VideoRecorder that save is complete
        payload.onSaved?.();
      }
    };

    const handleRecordAgain = () => {
      setPreviewUrl(null);
      onChange(null);
    };

    const getVideoUrl = (response: any): string | null => {
      if (!response) return null;
      if (typeof response === 'string') return response;
      if (typeof response === 'object' && response.videoUrl) {
        return response.videoUrl;
      }
      return null;
    };

    const rawVideoPath = getVideoUrl(response);

    const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    // Poll video recorder upload state
    useEffect(() => {
      const interval = setInterval(() => {
        if (ref && typeof ref !== 'function' && ref.current) {
          const uploading = ref.current.isUploading();
          setIsUploadingVideo(uploading);
        }
      }, 100);
      return () => clearInterval(interval);
    }, [ref]);

    // Notify parent of upload state changes
    useEffect(() => {
      onUploadStateChange?.(isUploadingVideo);
    }, [isUploadingVideo, onUploadStateChange]);

    // Clear video state when question changes
    useEffect(() => {
      setPreviewUrl(null);
      setResolvedUrl(null);
      setIsUploadingVideo(false); // Reset upload state for new question
      setUploadFailed(false);
      onUploadStateChange?.(false); // Notify parent immediately
    }, [questionId, onUploadStateChange]);

    useEffect(() => {
      let active = true;
      const resolve = async () => {
        if (!rawVideoPath) {
          setResolvedUrl(null);
          return;
        }
        // If already an absolute URL, use as-is
        if (/^https?:\/\//i.test(rawVideoPath)) {
          setResolvedUrl(rawVideoPath);
          return;
        }
        try {
          // Assume file path in private 'test-responses' bucket
          const url = await generateSecureFileUrl('test-responses', rawVideoPath, { testResponseId });
          if (active) setResolvedUrl(url);
        } catch (e) {
          console.error('Failed to resolve secure video URL', e);
          if (active) setResolvedUrl(null);
        }
      };
      resolve();
      return () => {
        active = false;
      };
    }, [rawVideoPath, testResponseId]);

    const playbackUrl = previewUrl || resolvedUrl;

    // Reset upload state when video is successfully loaded for playback
    useEffect(() => {
      if (playbackUrl) {
        console.log('[VideoQuestion] Playback URL available, clearing upload state');
        setIsUploadingVideo(false);
        setUploadFailed(false);
        onUploadStateChange?.(false);
      }
    }, [playbackUrl, onUploadStateChange]);

    return (
      <div className="space-y-4">
        <h3 className="font-medium text-2xl text-foreground">{content.question}</h3>
        
        {/* Conditionally render VideoRecorder based on camera readiness */}
        <div className={playbackUrl ? "opacity-0 pointer-events-none h-0 overflow-hidden" : ""}>
          {isVideoRecorderReady ? (
            <VideoRecorder
              ref={ref} 
              onRecordingComplete={handleRecordingComplete} 
              testResponseId={testResponseId}
              questionId={questionId}
              onRecordingStart={onRecordingStart}
              onRecordingStop={onRecordingStop}
              onUploadProgress={onUploadProgress}
            />
          ) : (
            <div className="aspect-video bg-muted flex items-center justify-center rounded-sm">
              <p className="text-muted-foreground text-sm">Initializing camera...</p>
            </div>
          )}
        </div>
        
        {/* Show video playback when available */}
        {playbackUrl && (
          <div className="space-y-2">
            <video 
              src={playbackUrl} 
              controls 
              className={cn("w-full rounded-sm")} 
            />
            <button
              onClick={handleRecordAgain}
              disabled={isUploadInProgress}
              className="text-sm text-destructive hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploadInProgress ? 'Upload in progress...' : 'Record again'}
            </button>
          </div>
        )}
      </div>
    );
  }
);

VideoQuestion.displayName = "VideoQuestion";
